import { Table, type TableProps, Tag, Typography } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line max-len
import { type LocalCustomerManagementModuleSpecsFragment } from '../../../../api/fragments/LocalCustomerManagementModuleSpecs';
import renderBooleanIcon from '../../../../utilities/renderBooleanIcon';
import useRenderKYCFieldName from '../../UpdateKYCPresetPage/useRenderKYCFieldName';
import KYCPresetsActions from './KYCPresetActions';

export type KYCPresetsTableProps = {
    module: LocalCustomerManagementModuleSpecsFragment;
};

export type KYCPresetSpecs = LocalCustomerManagementModuleSpecsFragment['kycPresets'][number];

const KYCPresetsTable = ({ module }: KYCPresetsTableProps) => {
    const { t } = useTranslation('localCustomerModuleDetails');

    const renderKYCFieldName = useRenderKYCFieldName();

    const expandable = useMemo(
        (): TableProps<KYCPresetSpecs>['expandable'] => ({
            expandedRowRender: record =>
                record.fields.map(({ key, isRequired }) => (
                    <Tag key={key} color={isRequired ? 'red' : 'geekblue'}>
                        <Typography.Text style={{ color: 'inherit' }}>{renderKYCFieldName(key)}</Typography.Text>
                    </Tag>
                )),
            rowExpandable: record => record.fields.length > 0,
        }),
        []
    );

    const renderActions = useCallback(
        (record: KYCPresetSpecs) => <KYCPresetsActions kycPreset={record} module={module} />,
        [module]
    );

    return (
        <Table dataSource={module.kycPresets} expandable={expandable} pagination={false} rowKey="id">
            <Table.Column
                key="displayName"
                dataIndex="displayName"
                title={t('localCustomerModuleDetails:kycPresetsTable.columns.displayName')}
            />
            <Table.Column<KYCPresetSpecs>
                key="fieldCount"
                render={record => record.fields.length}
                title={t('localCustomerModuleDetails:kycPresetsTable.columns.fieldCount')}
            />
            <Table.Column
                key="isActive"
                align="center"
                render={record => renderBooleanIcon(record.isActive, true)}
                title={t('localCustomerModuleDetails:kycPresetsTable.columns.isActive')}
                width={120}
            />
            <Table.Column
                key="actions"
                align="center"
                render={renderActions}
                title={t('localCustomerModuleDetails:kycPresetsTable.columns.actions')}
                width={100}
            />
        </Table>
    );
};

export default KYCPresetsTable;
